import { JSX, useEffect, useRef, useState } from "react"
import { Navigate } from "react-router-dom"
import { Box, Drawer, HStack, Portal, Stack } from "@chakra-ui/react"

import { getSecondaryDeals } from "@/api"
import { NavBar, Navigation } from "@/components"
import { useAuth } from "@/contexts"
import { useClientDeals, useIsMobile } from "@/hooks"
import { Deal, NavOption } from "@/types"

import { AccountPage, CompliancePage, DealsPage, DocumentsPage, NdaPage, PaymentCenterPage, ResourcesPage, SecondaryPage, TaxesPage } from "./"

interface Props {
  currentNavOption: string
}

export const DashboardPage = (props: Props) => {
  const { user } = useAuth()
  const { data: clientDeals, isLoading, refetch } = useClientDeals()
  const sectionRef = useRef<HTMLDivElement>(null)
  const isMobile = useIsMobile()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [currentDeal, setCurrentDeal] = useState<Deal | null>(null)

  // Default to the most recent deal (highest id) when deals load
  useEffect(() => {
    if (clientDeals && clientDeals.length > 0 && !currentDeal) {
      const mostRecent = clientDeals.reduce((latest, deal) =>
        deal.id > latest.id ? deal : latest, clientDeals[0]
      )
      setCurrentDeal(mostRecent)
    }
  }, [clientDeals])

  // Keep currentDeal in sync when deals data refreshes
  useEffect(() => {
    if (currentDeal && clientDeals && clientDeals.length > 0) {
      const updated = clientDeals.find(d => d.id === currentDeal.id)
      if (updated && updated !== currentDeal) {
        setCurrentDeal(updated)
      }
    }
  }, [clientDeals])

  const componentMap: Record<string, JSX.Element> = {
    [NavOption.DOCUMENTS]: <DocumentsPage deals={clientDeals} isLoading={isLoading} onRefresh={refetch} currentDeal={currentDeal} onDealChange={setCurrentDeal} />,
    [NavOption.CURRENT_OFFER]: <DealsPage deals={clientDeals} isLoading={isLoading} onRefresh={refetch} currentDeal={currentDeal} onDealChange={setCurrentDeal} />,
    [NavOption.RESOURCES]: <ResourcesPage />,
    [NavOption.TAX_CALCULATION]: <TaxesPage />,
    [NavOption.NDA]: <NdaPage />,
    [NavOption.COMPLIANCE]: <CompliancePage deals={clientDeals} isLoading={isLoading} onRefresh={refetch} currentDeal={currentDeal} onDealChange={setCurrentDeal} />,
    [NavOption.PAYMENT_CENTER]: <PaymentCenterPage deals={clientDeals} isLoading={isLoading} onRefresh={refetch} />,
    [NavOption.ACCOUNT]: <AccountPage />,
    [NavOption.SECONDARY]: <SecondaryPage />,
  }

  // Secondary-only clients (no portal deals, seeded from the Airtable mirror)
  // land on /payment via the rollout gate — reroute them to the Secondary Fund
  // payment center instead of the Documents fallback.
  const [hasSecondaryDeals, setHasSecondaryDeals] = useState(false)
  useEffect(() => {
    if (user?.userType !== "client") return
    getSecondaryDeals()
      .then(res => setHasSecondaryDeals(res.deals.length > 0))
      .catch(() => setHasSecondaryDeals(false))
  }, [user?.userType])

  const hasActiveDeal = clientDeals.some(d => d.isActive === 1)
  const effectiveNavOption = props.currentNavOption === NavOption.PAYMENT_CENTER && !hasActiveDeal
    ? (hasSecondaryDeals ? NavOption.SECONDARY : NavOption.DOCUMENTS)
    : props.currentNavOption
  const selectedComponent = componentMap[effectiveNavOption] || <div>Component not found</div>;

  // Refresh deals data when navigating between tabs
  useEffect(() => {
    refetch()
    sectionRef.current?.scrollTo({ top: 0, behavior: "instant" })
  }, [props.currentNavOption])

  if (!user) {
    return <div>Loading...</div>
  }

  // A deal manager can reach the client routes because ProtectedRoute only
  // checks that someone is logged in, not what they are. Send them to their own
  // landing page, the same one login uses.
  //
  // View-As is safe from this redirect as of 3193beb: the compliance buttons that
  // used to navigate() to a client ROUTE (and so land HERE, bouncing a deal
  // manager mid-View-As out to /eso/deals) now branch on the viewed client —
  // navigate(viewedClient ? `/clients/${viewedClient.id}` : "/documents") — in all
  // six of them. The View-As chrome is ViewAsNavBar, not the client NavBar, so
  // NavBar's own navigate("/documents") does not fire there either.
  //
  // If you add a client-page button that navigates by route, branch on
  // useViewer().viewedClient the same way, or it will bounce the operator out.
  if (user.userType === "deal_manager") {
    return <Navigate to="/eso/deals" replace />
  }

  // Deliberately a message and not a redirect: DealManagerDashboardPage sends
  // clients here, so redirecting an unrecognised role onward would bounce
  // between the two dashboards forever.
  if (user.userType !== "client") {
    return <div>Please contact support. Your account is not set up for this area.</div>
  }

  return (
    <HStack w="100%" h="100vh" gap={0}>
      {!isMobile && (
        <Box w="20vw" h="100%">
          <Navigation dealManager={user.dealManager} deals={clientDeals || []} />
        </Box>
      )}
      <Stack w={isMobile ? "100%" : "80vw"} h="100%">
        <NavBar
          deals={clientDeals || []}
          currentDeal={currentDeal}
          onDealChange={setCurrentDeal}
          onRefresh={refetch}
          onOpenDrawer={isMobile ? () => setIsDrawerOpen(true) : undefined}
        />
        <Stack ref={sectionRef} bg="white" w="100%" overflowY="scroll">
          {selectedComponent}
        </Stack>
      </Stack>

      {isMobile && (
        <Drawer.Root
          open={isDrawerOpen}
          onOpenChange={(e) => setIsDrawerOpen(e.open)}
          placement="start"
        >
          <Portal>
            <Drawer.Backdrop />
            <Drawer.Positioner>
              <Drawer.Content bg="darkBlue.200" maxW="280px">
                <Drawer.Body p={0}>
                  <Navigation
                    dealManager={user.dealManager}
                    deals={clientDeals || []}
                    onNavItemClick={() => setIsDrawerOpen(false)}
                  />
                </Drawer.Body>
              </Drawer.Content>
            </Drawer.Positioner>
          </Portal>
        </Drawer.Root>
      )}
    </HStack>
  )
}
